import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Video, Wifi, WifiOff, Mic, MicOff, MessageSquare, X, Loader, Send, Check,
} from 'lucide-react';
import {
  deployBot,
  removeBotFromMeeting,
  getTranscriptAndStatus,
  botChat,
  VexaPlatform,
  TranscriptSegment,
  TranscriptAndStatus,
} from '../../services/vexa';

interface SessionAvatar {
  role: string;
  name: string;
  imageUrl?: string;
  primaryColor?: string;
}

interface MeetingsTabProps {
  sessionId: string;
  sessionAvatars: SessionAvatar[];
  /** Lifted connection state — persists across tab switches */
  meetingConnection: { platform: VexaPlatform; meetingId: string } | null;
  onMeetingConnectionChange: (conn: { platform: VexaPlatform; meetingId: string } | null) => void;
  meetingChatEnabled: boolean;
  onMeetingChatEnabledChange: (enabled: boolean) => void;
  meetingSpeakEnabled: boolean;
  onMeetingSpeakEnabledChange: (enabled: boolean) => void;
  /** Per-avatar relay: Set of role strings that should relay through the meeting */
  meetingRelayAvatars: Set<string>;
  onMeetingRelayAvatarsChange: (avatars: Set<string>) => void;
}

const PLATFORMS: { value: VexaPlatform; label: string; idPlaceholder: string; passcodeRequired: boolean; passcodeLabel: string }[] = [
  { value: 'teams', label: 'Microsoft Teams', idPlaceholder: 'e.g. 1234567890123', passcodeRequired: true, passcodeLabel: 'Passcode (required)' },
  { value: 'google_meet', label: 'Google Meet', idPlaceholder: 'e.g. abc-defg-hij', passcodeRequired: false, passcodeLabel: '' },
  { value: 'zoom', label: 'Zoom', idPlaceholder: 'e.g. 12345678901', passcodeRequired: false, passcodeLabel: 'Passcode (optional)' },
];

const STATUS_LABELS: Record<string, string> = {
  connecting: 'Connecting',
  joined: 'Joined',
  transcribing: 'Transcribing',
  disconnected: 'Disconnected',
  failed: 'Failed',
  requested: 'Requested',
};

const STATUS_COLORS: Record<string, string> = {
  connecting: 'bg-yellow-100 text-yellow-800',
  joined: 'bg-green-100 text-green-800',
  transcribing: 'bg-primary/10 text-primary-dark',
  disconnected: 'bg-slate-100 text-slate-600',
  failed: 'bg-red-100 text-red-800',
  requested: 'bg-blue-100 text-blue-800',
};

/**
 * Normalizes the poll response, which can arrive as either a flat array of
 * transcript segments, or an object shape with `status`/`segments`.
 */
function normalizePollResponse(
  raw: TranscriptAndStatus | TranscriptSegment[],
): { status: string; segments: TranscriptSegment[] } {
  if (Array.isArray(raw)) {
    // If we're getting transcripts, bot is joined
    return { status: 'joined', segments: raw };
  }
  return {
    status: raw.status || 'joined',
    segments: Array.isArray(raw.segments) ? raw.segments : [],
  };
}

function TestButtonIcon({ isSending, wasSent }: { isSending: boolean; wasSent: boolean }) {
  if (isSending) return <Loader size={12} className="animate-spin" />;
  if (wasSent) return <Check size={12} />;
  return <Send size={12} />;
}

interface ConnectionFormProps {
  platform: VexaPlatform;
  setPlatform: React.Dispatch<React.SetStateAction<VexaPlatform>>;
  meetingId: string;
  setMeetingId: React.Dispatch<React.SetStateAction<string>>;
  passcode: string;
  setPasscode: React.Dispatch<React.SetStateAction<string>>;
  botName: string;
  setBotName: React.Dispatch<React.SetStateAction<string>>;
  transcribeEnabled: boolean;
  setTranscribeEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  recordingEnabled: boolean;
  setRecordingEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  connecting: boolean;
  onConnect: () => void;
}

function ConnectionForm({
  platform,
  setPlatform,
  meetingId,
  setMeetingId,
  passcode,
  setPasscode,
  botName,
  setBotName,
  transcribeEnabled,
  setTranscribeEnabled,
  recordingEnabled,
  setRecordingEnabled,
  connecting,
  onConnect,
}: ConnectionFormProps) {
  const platformConfig = PLATFORMS.find(p => p.value === platform)!;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-5">
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Connect to Meeting</h3>

      {/* Platform */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Platform</label>
        <select
          value={platform}
          onChange={(e) => { setPlatform(e.target.value as VexaPlatform); setPasscode(''); }}
          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        >
          {PLATFORMS.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>

      {/* Meeting ID */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Meeting ID</label>
        <input
          type="text"
          value={meetingId}
          onChange={(e) => setMeetingId(e.target.value)}
          placeholder={platformConfig.idPlaceholder}
          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>

      {/* Passcode (conditional) */}
      {platform !== 'google_meet' && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">{platformConfig.passcodeLabel}</label>
          <input
            type="text"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder={platform === 'teams' ? 'From ?p= in meeting URL' : 'From ?pwd= in meeting URL'}
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
      )}

      {/* Bot Name */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Bot Display Name</label>
        <input
          type="text"
          value={botName}
          onChange={(e) => setBotName(e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>

      {/* Options */}
      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={transcribeEnabled} onChange={(e) => setTranscribeEnabled(e.target.checked)} className="w-4 h-4 accent-primary rounded" />
          Transcription
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={recordingEnabled} onChange={(e) => setRecordingEnabled(e.target.checked)} className="w-4 h-4 accent-primary rounded" />
          Recording
        </label>
      </div>

      {/* Connect */}
      <button
        onClick={onConnect}
        disabled={connecting || !meetingId.trim() || (platform === 'teams' && !passcode.trim())}
        className="w-full px-4 py-3 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        {connecting ? <><Loader size={16} className="animate-spin" /> Connecting...</> : <>
          <Video size={16} /> Connect Bot to Meeting
        </>}
      </button>
    </div>
  );
}

interface AvatarRelayRowProps {
  avatar: SessionAvatar;
  isRelayed: boolean;
  isSending: boolean;
  wasSent: boolean;
  onToggleRelay: () => void;
  onSendTest: () => void;
}

function AvatarRelayRow({
  avatar,
  isRelayed,
  isSending,
  wasSent,
  onToggleRelay,
  onSendTest,
}: AvatarRelayRowProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        {avatar.imageUrl ? (
          <img src={avatar.imageUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
            style={{ backgroundColor: avatar.primaryColor || '#64748b' }}
          >
            {avatar.name.charAt(0)}
          </div>
        )}
        <div>
          <span className="text-sm font-medium text-slate-700">{avatar.name}</span>
          <p className="text-[10px] text-slate-400">
            {isRelayed ? 'Relaying to meeting' : 'Browser / device playback'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* Test message button */}
        <button
          onClick={onSendTest}
          disabled={isSending}
          className={`flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-lg transition-colors ${
            wasSent
              ? 'bg-green-50 text-green-600 border border-green-200'
              : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100'
          } disabled:opacity-50`}
          title="Send a test message to the meeting"
        >
          <TestButtonIcon isSending={isSending} wasSent={wasSent} />
          {wasSent ? 'Sent' : 'Test'}
        </button>
        {/* Relay toggle */}
        <button
          onClick={onToggleRelay}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            isRelayed ? 'bg-primary' : 'bg-slate-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isRelayed ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

interface ActiveConnectionPanelProps {
  meetingConnection: { platform: VexaPlatform; meetingId: string };
  status: string;
  onDisconnect: () => void;
  meetingSpeakEnabled: boolean;
  onMeetingSpeakEnabledChange: (enabled: boolean) => void;
  meetingChatEnabled: boolean;
  onMeetingChatEnabledChange: (enabled: boolean) => void;
  sessionAvatars: SessionAvatar[];
  meetingRelayAvatars: Set<string>;
  onToggleAvatarRelay: (role: string) => void;
  testingSend: Set<string>;
  testSent: Set<string>;
  onSendTest: (avatar: SessionAvatar) => void;
  transcriptSegments: TranscriptSegment[];
  transcriptEndRef: React.RefObject<HTMLDivElement | null>;
}

function ActiveConnectionPanel({
  meetingConnection,
  status,
  onDisconnect,
  meetingSpeakEnabled,
  onMeetingSpeakEnabledChange,
  meetingChatEnabled,
  onMeetingChatEnabledChange,
  sessionAvatars,
  meetingRelayAvatars,
  onToggleAvatarRelay,
  testingSend,
  testSent,
  onSendTest,
  transcriptSegments,
  transcriptEndRef,
}: ActiveConnectionPanelProps) {
  return (
    <>
      {/* Status Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wifi size={18} className="text-green-500" />
            <div>
              <h3 className="text-sm font-semibold text-slate-800">
                {PLATFORMS.find(p => p.value === meetingConnection.platform)?.label}
              </h3>
              <p className="text-xs text-slate-500 font-mono">{meetingConnection.meetingId}</p>
            </div>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[status] || STATUS_COLORS.connecting}`}>
              {STATUS_LABELS[status] || status}
            </span>
          </div>
          <button
            onClick={onDisconnect}
            className="text-sm font-medium text-red-600 hover:text-red-700 hover:underline flex items-center gap-1"
          >
            <WifiOff size={14} /> Disconnect
          </button>
        </div>

        {/* Relay Toggles */}
        <div className="flex gap-3 pt-2 border-t border-slate-100">
          <button
            onClick={() => onMeetingSpeakEnabledChange(!meetingSpeakEnabled)}
            className={`flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-lg transition-colors ${
              meetingSpeakEnabled ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-slate-50 text-slate-500 border border-slate-200'
            }`}
          >
            {meetingSpeakEnabled ? <Mic size={14} /> : <MicOff size={14} />}
            Speak in Meeting
          </button>
          <button
            onClick={() => onMeetingChatEnabledChange(!meetingChatEnabled)}
            className={`flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-lg transition-colors ${
              meetingChatEnabled ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-slate-50 text-slate-500 border border-slate-200'
            }`}
          >
            <MessageSquare size={14} />
            Chat in Meeting
          </button>
        </div>
      </div>

      {/* Avatar Relay Config */}
      {sessionAvatars.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">
            Avatar Meeting Relay
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Toggle which avatars relay through the meeting. Enabled avatars will send contributions
            to the meeting chat instead of playing through the browser or physical devices.
          </p>
          <div className="divide-y divide-slate-100">
            {sessionAvatars.map(avatar => {
              const isRelayed = meetingRelayAvatars.has(avatar.role);
              const isSending = testingSend.has(avatar.role);
              const wasSent = testSent.has(avatar.role);
              return (
                <AvatarRelayRow
                  key={avatar.role}
                  avatar={avatar}
                  isRelayed={isRelayed}
                  isSending={isSending}
                  wasSent={wasSent}
                  onToggleRelay={() => onToggleAvatarRelay(avatar.role)}
                  onSendTest={() => onSendTest(avatar)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Transcript Feed */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">Live Transcript</h3>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {transcriptSegments.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-12">Waiting for transcript...</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {transcriptSegments.map((seg) => (
                <div key={`${seg.timestamp}-${seg.speaker}`} className="px-6 py-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-semibold text-slate-700">{seg.speaker}</span>
                    <span className="text-[10px] text-slate-400">
                      {new Date(seg.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">{seg.text}</p>
                </div>
              ))}
              <div ref={transcriptEndRef} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const MeetingsTab: React.FC<MeetingsTabProps> = ({
  sessionId,
  sessionAvatars,
  meetingConnection,
  onMeetingConnectionChange,
  meetingChatEnabled,
  onMeetingChatEnabledChange,
  meetingSpeakEnabled,
  onMeetingSpeakEnabledChange,
  meetingRelayAvatars,
  onMeetingRelayAvatarsChange,
}) => {
  // Local UI state
  const [status, setStatus] = useState<string>(meetingConnection ? 'connecting' : 'disconnected');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [platform, setPlatform] = useState<VexaPlatform>(meetingConnection?.platform || 'teams');
  const [meetingId, setMeetingId] = useState(meetingConnection?.meetingId || '');
  const [passcode, setPasscode] = useState('');
  const [botName, setBotName] = useState(`Spectrum-${sessionId.slice(0, 6)}`);
  const [transcribeEnabled, setTranscribeEnabled] = useState(true);
  const [recordingEnabled, setRecordingEnabled] = useState(false);

  // Live data (local — repopulated by polling)
  const [transcriptSegments, setTranscriptSegments] = useState<TranscriptSegment[]>([]);

  // Test message state per avatar
  const [testingSend, setTestingSend] = useState<Set<string>>(new Set());
  const [testSent, setTestSent] = useState<Set<string>>(new Set());

  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isConnected = meetingConnection !== null;

  const toggleAvatarRelay = (role: string) => {
    const next = new Set(meetingRelayAvatars);
    if (next.has(role)) {
      next.delete(role);
    } else {
      next.add(role);
    }
    onMeetingRelayAvatarsChange(next);
  };

  const handleSendTest = async (avatar: SessionAvatar) => {
    if (!meetingConnection) return;
    setTestingSend(prev => new Set(prev).add(avatar.role));
    try {
      await botChat(
        meetingConnection.platform,
        meetingConnection.meetingId,
        `[${avatar.name}] Hello from ${avatar.name}! This is a test message.`,
      );
      setTestSent(prev => new Set(prev).add(avatar.role));
      setTimeout(() => {
        setTestSent(prev => {
          const next = new Set(prev);
          next.delete(avatar.role);
          return next;
        });
      }, 3000);
    } catch (err) {
      console.error(`[vexa] Test message failed for ${avatar.role}:`, err);
    } finally {
      setTestingSend(prev => {
        const next = new Set(prev);
        next.delete(avatar.role);
        return next;
      });
    }
  };

  const cleanup = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    onMeetingConnectionChange(null);
    onMeetingChatEnabledChange(false);
    onMeetingSpeakEnabledChange(false);
    setStatus('disconnected');
    setTranscriptSegments([]);
  }, [onMeetingConnectionChange, onMeetingChatEnabledChange, onMeetingSpeakEnabledChange]);

  const handleConnect = async () => {
    if (!meetingId.trim()) return;
    if (platform === 'teams' && !passcode.trim()) {
      setError('Passcode is required for Teams meetings');
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      await deployBot(
        platform,
        meetingId.trim(),
        passcode.trim() || undefined,
        botName.trim() || undefined,
        { transcribeEnabled, recordingEnabled },
      );
      onMeetingConnectionChange({ platform, meetingId: meetingId.trim() });
      setStatus('connecting');
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setError('Feature not enabled for your account');
      } else {
        setError(err?.response?.data?.error || err.message || 'Failed to connect');
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!meetingConnection) return;
    try {
      await removeBotFromMeeting(meetingConnection.platform, meetingConnection.meetingId);
    } catch (err) {
      console.error('[vexa] Error removing bot:', err);
    }
    cleanup();
  };

  // Poll status and transcript when connected
  useEffect(() => {
    if (!meetingConnection) return;

    const poll = async () => {
      try {
        const raw = await getTranscriptAndStatus(meetingConnection.platform, meetingConnection.meetingId);

        const { status: s, segments } = normalizePollResponse(raw);

        setStatus(s);
        if (s === 'failed' || s === 'disconnected') {
          cleanup();
          return;
        }
        setTranscriptSegments(segments);
      } catch (err) {
        console.error('[vexa] Poll error:', err);
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, 10_000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [meetingConnection, cleanup]);

  // Cleanup interval on unmount (but do NOT clear connection — it lives in parent)
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcriptSegments]);

  return (
    <div className="flex-1 overflow-y-auto bg-surface-light p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Video size={22} className="text-primary" />
            Meeting Integration
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Connect a bot to a live meeting to capture transcripts and relay avatar responses.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center justify-between bg-red-50 text-red-700 text-sm px-4 py-3 rounded-xl border border-red-200">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="hover:text-red-900"><X size={16} /></button>
          </div>
        )}

        {!isConnected ? (
          /* ============ CONNECTION FORM ============ */
          <ConnectionForm
            platform={platform}
            setPlatform={setPlatform}
            meetingId={meetingId}
            setMeetingId={setMeetingId}
            passcode={passcode}
            setPasscode={setPasscode}
            botName={botName}
            setBotName={setBotName}
            transcribeEnabled={transcribeEnabled}
            setTranscribeEnabled={setTranscribeEnabled}
            recordingEnabled={recordingEnabled}
            setRecordingEnabled={setRecordingEnabled}
            connecting={connecting}
            onConnect={handleConnect}
          />
        ) : (
          /* ============ ACTIVE CONNECTION ============ */
          <ActiveConnectionPanel
            meetingConnection={meetingConnection}
            status={status}
            onDisconnect={handleDisconnect}
            meetingSpeakEnabled={meetingSpeakEnabled}
            onMeetingSpeakEnabledChange={onMeetingSpeakEnabledChange}
            meetingChatEnabled={meetingChatEnabled}
            onMeetingChatEnabledChange={onMeetingChatEnabledChange}
            sessionAvatars={sessionAvatars}
            meetingRelayAvatars={meetingRelayAvatars}
            onToggleAvatarRelay={toggleAvatarRelay}
            testingSend={testingSend}
            testSent={testSent}
            onSendTest={handleSendTest}
            transcriptSegments={transcriptSegments}
            transcriptEndRef={transcriptEndRef}
          />
        )}
      </div>
    </div>
  );
};

export default MeetingsTab;
